import PageSEO from '@/components/PageSEO';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Phone, Users, TrendingUp, Heart, Award } from 'lucide-react';

export const CareersPage = () => {
  useEffect(() => {
    // Load SociableKit Indeed Jobs widget script
    const script = document.createElement('script');
    script.src = 'https://widgets.sociablekit.com/indeed-jobs/widget.js';
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Career Growth',
      description: 'On-the-job training and skill-based advancement opportunities.',
    },
    {
      icon: Users,
      title: 'Team Culture',
      description: 'Join a supportive team that values unity and collaboration.',
    },
    {
      icon: Heart,
      title: 'Work-Life Balance',
      description: 'Monday-Friday schedule with PTO and paid holidays.',
    },
    {
      icon: Award,
      title: 'Competitive Pay',
      description: 'Weekly pay with performance incentives and benefits.',
    },
  ];

  const perks = [
    'Paid time off (PTO)',
    'Paid holidays',
    'Performance incentives',
    'Weekly pay',
    'Training programs',
    'Growth opportunities',
    'Supportive team environment',
    'Full-time positions',
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Careers" description="Join the Comal Glass Company team. View current job openings for glass installers, estimators, and more in Bulverde, TX and the Hill Country." path="/careers" />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6">Join Our Team</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Build Your <span className="text-gradient-cyan">Career</span> With Us
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Join the Comal Glass Company team and be part of a growing company that values craftsmanship, teamwork, and professional development. No experience necessary – we'll train you!
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Why Join Us</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why Work at <span className="text-gradient-cyan">Comal Glass</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We're more than just a glass company – we're a team dedicated to excellence and growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Positions - Indeed Widget */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <Badge className="mb-4">Open Positions</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Current <span className="text-gradient-cyan">Job Openings</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our available positions and find the right fit for your skills and career goals.
            </p>
          </div>

          {/* Indeed Jobs Widget */}
          <div className="max-w-4xl mx-auto">
            <div className="sk-ww-indeed-jobs" data-embed-id="25645809"></div>
          </div>
        </div>
      </section>

      {/* Benefits & Perks */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Benefits</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Benefits & <span className="text-gradient-cyan">Perks</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {perks.map((perk, index) => (
              <Card key={index} className="border-none bg-card">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <p className="text-sm font-medium">{perk}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Ideal Candidates</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                What We <span className="text-gradient-cyan">Look For</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Willingness to Learn</h3>
                    <p className="text-muted-foreground text-sm">
                      No experience needed – we provide comprehensive on-the-job training.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Strong Work Ethic</h3>
                    <p className="text-muted-foreground text-sm">
                      Reliable, punctual, and committed to quality craftsmanship.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Team Player</h3>
                    <p className="text-muted-foreground text-sm">
                      Works well with others and contributes to a positive work environment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Physical Capability</h3>
                    <p className="text-muted-foreground text-sm">
                      Ability to lift up to 85 lbs and work in various environments.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/mirrors/mirror_installation.jpg"
                alt="Comal Glass team at work"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to <span className="text-gradient-cyan">Get Started?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Email our hiring team at{' '}
              <a href="mailto:careers@comalglass.com" className="text-primary hover:underline font-semibold">
                careers@comalglass.com
              </a>{' '}
              or apply to one of our open positions listed above.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
